import {Button, createStyles, Group, Stack, Text, Title} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import CalendarStore from 'calendar/CalendarStore';
import EntryCalendar from 'calendar/EntryCalendar';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import EntityStore from 'stores/EntityStore';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import ActivitySection from './homepage/ActivitySection';
import {shouldShowWorklistsSection} from './homepage/HomepageVisibility';
import MyWorkSection from './homepage/MyWorkSection';
import RecentsSection from './homepage/RecentsSection';
import WorklistsSection from './homepage/WorklistsSection';

const COLUMN_SPACING = '26px';

const useStyles = createStyles((theme) => ({
  clickable: {
    cursor: 'pointer',
  },
  flexGrow: {
    flexGrow: 1,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  flexStart: {
    alignSelf: 'flex-start',
  },
  negativeXsMarginLeft: {
    marginLeft: `-${theme.spacing.xs}`,
  },
  xsMarginRight: {
    marginRight: theme.spacing.xs,
  },
  columnContainer: {
    padding: `${theme.spacing.lg} ${COLUMN_SPACING}`,
    backgroundColor: theme.colors.gray[0],
  },
  column: {
    width: `calc(50% - ${COLUMN_SPACING} / 2)`,

    // Under 1000px, we show a single full-width column.
    '@media (max-width: 1000px)': {
      width: '100%',
    },
  },
}));

const HomepageCards = (): JSX.Element => {
  const {classes, cx} = useStyles();

  return (
    <Group
      position="center"
      align="flex-start"
      spacing={COLUMN_SPACING}
      className={cx(classes.columnContainer, classes.flexGrow)}
    >
      <Stack className={classes.column}>
        <RecentsSection />
        {shouldShowWorklistsSection() && <WorklistsSection />}
      </Stack>
      <Stack className={classes.column}>
        <MyWorkSection />
        <ActivitySection />
      </Stack>
    </Group>
  );
};

/** Preference-gated home sidepanel with recents, worklists, my work, and activity sections. */
export default function Homepage(): JSX.Element {
  const [showCalendar, setShowCalendar] = useState<boolean>(false);

  const user = useStoreSelector(
    EntityStore,
    useCallback((store) => store.currentUser(), [])
  );
  const todayDate = useStoreSelector(
    CalendarStore,
    useCallback((store) => store.getToday(), [])
  );

  useOnMount(() => {
    SidepanelActions.expand();
  });

  const {classes, cx} = useStyles();

  return (
    <div className={cx(classes.container, 'sidepanel')}>
      <Stack m="24px" spacing="0">
        <Group position="apart" noWrap={true}>
          <Group align="flex-end">
            <Title order={3}>Welcome back{user != null ? `, ${user.name}` : ''}</Title>
            <Text color="dimmed" size="14px" lh="26px">
              {todayDate.format('MMMM D, YYYY')}{' '}
            </Text>
            <Button
              size="xs"
              variant="ghost"
              className={classes.negativeXsMarginLeft}
              onClick={() => {
                setShowCalendar(!showCalendar);
              }}
            >
              {showCalendar ? (
                'Back to home'
              ) : (
                <>
                  <BenchlingIcon className={classes.xsMarginRight} icon="calendar" /> My Calendar
                </>
              )}
            </Button>
          </Group>
          <BenchlingIcon
            icon="close"
            altText="Close"
            className={cx(classes.flexStart, classes.clickable)}
            style={{height: '18px', width: '18px'}}
            {...getActionButtonRoleProps({
              onPress: () => {
                SidepanelActions.close();
              },
            })}
          />
        </Group>
      </Stack>
      {showCalendar ? <EntryCalendar /> : <HomepageCards />}
    </div>
  );
}
